import { isAnalyticsEnabled } from "@shared/analytics/posthog";
import { useWalletLifecycleAnalytics } from "@shared/analytics/useWalletLifecycleAnalytics";
import { walletJourney } from "@shared/analytics/walletJourney";
import {
    ErrorBoundary,
    PageErrorFallback,
    ParticipantAccountStatus,
    UnsavedChangesProvider,
    useSessionStore,
    useTokenBalancesController,
    useWallet,
    useWalletModal
} from "@solomon-apps/shared";
import posthog from "posthog-js";
import React, { useEffect } from "react";
import { Outlet } from "react-router-dom";

import { useAccessControl } from "@/hooks/access/useAccessControl";
import { useYaasStatus } from "@/hooks/yaas/useYaasStatus";
import { useTermsStore } from "@/stores/useTermsStore";

import { NavigationBar } from "./NavigationBar";
import { SimRealHooksProvider } from "./SimRealHooksProvider";
import { TermsOfServiceModal } from "./TermsOfServiceModal";

function mapYaasStatus(s: ParticipantAccountStatus | null): string {
    if (s === null) return "unknown";
    if (s === ParticipantAccountStatus.Active) return "active";
    if (s === ParticipantAccountStatus.Pending) return "pending";
    return "blocked";
}

/**
 * V3 layout shell. Renders the v3 NavigationBar persistently above
 * the page content (keeps sliding underline animation alive across
 * navigation) and runs app-level initialization so individual pages
 * don't need to handle bootstrapping.
 */
export function V3Layout(): React.ReactElement {
    const { initialize, getVaultPermissions } = useSessionStore();
    const { connected, publicKey, disconnect, isWalletReady } = useWallet();
    const { refreshIfStale } = useTokenBalancesController();
    const {
        isConnectingFlow,
        showModal: isConnectModalOpen,
        registerAccessCheck,
        reportAccessCheck
    } = useWalletModal();
    const {
        isAllowed,
        isChecked: isAccessChecked,
        hasAccessCheckError
    } = useAccessControl();
    const { yaasStatus, isYaasStatusLoaded } = useSessionStore();
    const {
        isTermsDisplayAccepted,
        showTermsModal,
        acceptTermsForDisplay,
        openTermsModal,
        closeTermsModal,
        checkStoredDisplayAcceptance
    } = useTermsStore();

    useWalletLifecycleAnalytics(
        isAllowed,
        isAccessChecked,
        hasAccessCheckError
    );
    useYaasStatus();

    useEffect(() => {
        if (!connected || !isYaasStatusLoaded || !isAnalyticsEnabled()) return;
        const owner = walletJourney.owner();
        let active = true;
        void owner.identity
            ?.then(() => {
                if (
                    active &&
                    walletJourney.owner().properties.wallet_session_id ===
                        owner.properties.wallet_session_id
                ) {
                    posthog.setPersonProperties({
                        yaas_status: mapYaasStatus(yaasStatus)
                    });
                    posthog.register({
                        yaas_status: mapYaasStatus(yaasStatus)
                    });
                }
            })
            .catch(() => undefined);
        return (): void => {
            active = false;
        };
    }, [connected, publicKey, isYaasStatusLoaded, yaasStatus]);

    useEffect(() => {
        checkStoredDisplayAcceptance();
    }, [checkStoredDisplayAcceptance]);

    useEffect(() => {
        if (
            connected &&
            !isTermsDisplayAccepted &&
            !isConnectingFlow &&
            isAllowed
        ) {
            openTermsModal();
        }
    }, [
        connected,
        isTermsDisplayAccepted,
        isConnectingFlow,
        isAllowed,
        openTermsModal
    ]);

    const handleTermsClose = async (): Promise<void> => {
        closeTermsModal();
        await walletJourney.disconnect(
            disconnect,
            "terms_rejected",
            undefined,
            { entry_point: "terms_modal" }
        );
    };

    // Register access check so the wallet modal waits for whitelist result
    useEffect(() => {
        registerAccessCheck();
    }, [registerAccessCheck]);

    // Report access check result only while the connecting modal is open.
    // Outside the modal flow (e.g. auto-reconnect on page refresh) the
    // result is not piped into modal context — the nav button no longer
    // depends on it, so there's nothing to update.
    useEffect(() => {
        if (isConnectingFlow && connected && isAccessChecked) {
            reportAccessCheck(hasAccessCheckError ? "error" : isAllowed);
        }
    }, [
        isConnectingFlow,
        connected,
        isAccessChecked,
        isAllowed,
        hasAccessCheckError,
        reportAccessCheck
    ]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    useEffect(() => {
        if (!connected) {
            return;
        }

        void refreshIfStale();
    }, [connected, refreshIfStale]);

    useEffect(() => {
        if (isWalletReady && publicKey) {
            void getVaultPermissions(publicKey);
        }
    }, [isWalletReady, publicKey, getVaultPermissions]);

    useEffect(() => {
        if (!connected) {
            return;
        }

        const handleRefresh = (): void => {
            void refreshIfStale();
        };
        const handleVisibilityChange = (): void => {
            if (document.visibilityState === "visible") {
                handleRefresh();
            }
        };

        window.addEventListener("focus", handleRefresh);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return (): void => {
            window.removeEventListener("focus", handleRefresh);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, [connected, refreshIfStale]);

    return (
        <SimRealHooksProvider>
            <UnsavedChangesProvider>
                <div
                    data-testid="app-shell"
                    className="min-h-dvh bg-surface-ground flex flex-col"
                >
                    <TermsOfServiceModal
                        open={showTermsModal}
                        onClose={handleTermsClose}
                        onAccept={acceptTermsForDisplay}
                        surface="main_app"
                        trigger="first_visit"
                    />
                    {/* While the connect modal is open, the wrapper rises to
                        z-[45] (between dialog overlay z-40 and content z-50)
                        so the inner Connect button's existing z-[45] can
                        actually escape its parent stacking context and stay
                        crisp during the connecting animation. Reverts to z-30
                        otherwise. */}
                    <div
                        className={`sticky md:static top-0 ${
                            isConnectModalOpen ? "z-[45]" : "z-30"
                        } bg-surface-ground`}
                    >
                        <NavigationBar />
                        <div className="h-px w-full bg-line-base" />
                    </div>
                    <ErrorBoundary fallback={PageErrorFallback}>
                        <Outlet />
                    </ErrorBoundary>
                </div>
            </UnsavedChangesProvider>
        </SimRealHooksProvider>
    );
}
